import { useState } from "react";
import moment from "moment";
import useTransactionsPaidHistory from "../../hooks/useTransactionsPaidHistory";
import GraphHistory from "./GraphHistory";
import DateRangePickerComponent, {
  DateRangeValue
} from "../../components/DateRangePicker/DateRangePicker";
import { Box, Divider, Typography } from "@mui/material";
import TableLastsResults from "./TableLastResults";
import AsyncResultsWrapper from "../../components/AsyncResultsWrapper/AsyncResultsWrapper";
import TableLastResult from "./TableLastResult";
import { useTranslation } from "react-i18next";
import { typography } from "../../utils/typography";
import { semanticColors } from "../../utils/colors";
import { useCompanyPageTitle } from "../../state/selectedPaymentConfiguration";

export default function Resume() {
  const { t } = useTranslation();
  const companyTitle = useCompanyPageTitle();
  const [dates, setDates] = useState<DateRangeValue>([
    moment().subtract(1, "month"),
    moment()
  ]);

  const { data: dataResults, loading, error } = useTransactionsPaidHistory({
    filters: { dates }
  });

  const sumTotal =
    dataResults &&
    dataResults.reduce((accumulator: any, object: any) => {
      return accumulator + object.value;
    }, 0);

  return (
    <Box
      sx={{
        px: { xs: "16px", sm: "24px" },
        py: "48px",
        display: "flex",
        flexDirection: "column",
        gap: "48px"
      }}
    >
      {/* Page title — named after the company being shown, since a
          professional can have more than one registered. */}
      <Typography
        sx={{
          ...typography.heading.xl,
          color: semanticColors.text.primary,
          textAlign: "center"
        }}
      >
        {companyTitle(t("paymentsAccount.title"))}
      </Typography>

      {/* Last Transfer */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Typography
          sx={{
            ...typography.heading.l,
            color: semanticColors.text.primary
          }}
        >
          {t("paymentsAccount.lastTransfer")}
        </Typography>
        <Divider sx={{ borderColor: "#EFEFF4" }} />
        <AsyncResultsWrapper loading={loading} error={error} data={dataResults}>
          <TableLastResult data={dataResults} />
        </AsyncResultsWrapper>
      </Box>

      {/* Net Value */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Typography
          sx={{
            ...typography.heading.l,
            color: semanticColors.text.primary
          }}
        >
          {t("paymentsAccount.netValue")}{" "}
          <span style={{ color: "#0DBDA7" }}>
            {new Intl.NumberFormat("pt-PT", {
              style: "currency",
              currency: "EUR"
            }).format(sumTotal || 0)}
          </span>
        </Typography>
        <Divider sx={{ borderColor: "#EFEFF4" }} />
        {/* Two 150px date fields plus the gap: enough for the full year, which
            the previous 300px clipped. */}
        <Box sx={{ width: { xs: "100%", sm: "330px" } }}>
          <DateRangePickerComponent value={dates} onChange={setDates} />
        </Box>
        <AsyncResultsWrapper loading={loading} error={error} data={dataResults}>
          <GraphHistory dataResults={dataResults} />
        </AsyncResultsWrapper>
      </Box>

      {/* Last 5 Movements */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Typography
          sx={{
            ...typography.heading.l,
            color: semanticColors.text.primary
          }}
        >
          {t("paymentsAccount.last5Movements")}
        </Typography>
        <Divider sx={{ borderColor: "#EFEFF4" }} />
        <AsyncResultsWrapper loading={loading} error={error} data={dataResults}>
          <TableLastsResults inputProps={dataResults} />
        </AsyncResultsWrapper>
      </Box>
    </Box>
  );
}
